import { GitMergeIcon, MarkGithubIcon } from '@primer/octicons-react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { Repository } from '../../utils/api-client/api-schemas'
import AzureLightIcon from '../icons/azure-light.svg?react'
import BitbucketIcon from '../icons/bitbucket-icon.svg?react'
import GitlabIcon from '../icons/gitlab-icon.svg?react'

export const SCMIconSizeSchema = z.enum(['small', 'medium', 'medium+', 'large'])
type SCMIconSize = z.infer<typeof SCMIconSizeSchema>

type SCMIconProps = {
  type: Repository['type']
  className?: string
  size?: SCMIconSize
  withAriaLabel?: boolean
}

/**
 * Returns the appropriate SCM icon component based on the repository type
 * @param type - The type of repository (azure, github, gitlab, etc.)
 * @param size - Icon size: 'small' (16px), 'medium' (20px), 'medium+' (24px), 'large' (32px)
 * @param className - Optional CSS class name to apply to the icon
 * @param withAriaLabel - Whether to add an aria-label to the icon. Turn on only if relevant to screen reader users (and tests).
 * @returns A React component representing the repository's SCM icon
 */
export const SCMIcon = ({ type, size = 'medium', className, withAriaLabel = false }: SCMIconProps) => {
  const sizeInPixels = match(size)
    .with('small', () => 16)
    .with('medium', () => 20)
    .with('medium+', () => 24)
    .with('large', () => 32)
    .exhaustive()

  return match(type)
    .with('azure', () => (
      <AzureLightIcon
        style={{ width: sizeInPixels, height: sizeInPixels }}
        className={className}
        aria-label={withAriaLabel ? 'Azure icon' : undefined}
      />
    ))
    .with('github', () => (
      <MarkGithubIcon
        size={sizeInPixels}
        className={className}
        aria-label={withAriaLabel ? 'GitHub icon' : undefined}
      />
    ))
    .with('gitlab', () => (
      <GitlabIcon
        style={{ width: sizeInPixels, height: sizeInPixels }}
        className={className}
        aria-label={withAriaLabel ? 'GitLab icon' : undefined}
      />
    ))
    .with('bitbucket', () => (
      <BitbucketIcon
        style={{ width: sizeInPixels, height: sizeInPixels }}
        className={className}
        aria-label={withAriaLabel ? 'Bitbucket icon' : undefined}
      />
    ))
    .with('git', () => (
      <GitMergeIcon size={sizeInPixels} className={className} aria-label={withAriaLabel ? 'Git icon' : undefined} />
    ))
    .otherwise(() => (
      <GitMergeIcon size={sizeInPixels} className={className} aria-label={withAriaLabel ? 'Git icon' : undefined} />
    ))
}
